// Activation metric analytics events
//
// Activation definition (data-informed default):
//   A user is "activated" when they have:
//   1. Viewed their strategy (core value moment)
//   2. Used at least 1 additional feature: Sage chat, document upload, OR support calculator
//
// Rationale: With only 2 paid conversions in the dataset, we define activation
// based on the product's core value loop. Strategy is the "aha moment" — it's
// the first time users see personalized, actionable settlement guidance. A second
// feature touch (Sage, documents, or support calc) signals engagement beyond
// curiosity. This compound metric avoids false positives from drive-by signups
// while remaining achievable for serious users.
//
// This should be revisited once we have 20+ paid conversions for statistical
// significance. Track the correlation between user_activated and purchase events
// in PostHog to validate or refine the criteria.

export type ActivationAction =
  | 'strategy_viewed'
  | 'sage_message_sent'
  | 'document_uploaded'
  | 'support_calculated';

export const ACTIVATION_CRITERIA = {
  /** User must have viewed their strategy */
  requiredAction: 'strategy_viewed' as const,
  /** User must also complete at least one of these */
  secondaryActions: [
    'sage_message_sent',
    'document_uploaded',
    'support_calculated',
  ] as const,
} as const;

export const activationEvents = {
  /** Fired once when a user meets all activation criteria */
  userActivated: (actions: ActivationAction[]) => ({
    event: 'user_activated',
    properties: {
      actions_completed: actions,
      activated_at: new Date().toISOString(),
      activation_version: 'v1', // bump when criteria change
    },
  }),

  /** Tracks each individual activation action as it happens */
  activationActionCompleted: (action: ActivationAction) => ({
    event: 'activation_action_completed',
    properties: {
      action,
      completed_at: new Date().toISOString(),
    },
  }),
};

export type ActivationEvent = ReturnType<typeof activationEvents[keyof typeof activationEvents]>;

/**
 * Pure helper: given the actions a user has now completed and the ones we've
 * already fired `activation_action_completed` for, return the actions that are
 * newly completed (so each action fires exactly once). Order-preserving and
 * dedupes within `completed`.
 */
export function newlyCompletedActions(
  completed: ActivationAction[],
  alreadyFired: ActivationAction[],
): ActivationAction[] {
  const seen = new Set<ActivationAction>(alreadyFired);
  const out: ActivationAction[] = [];
  for (const action of completed) {
    if (!seen.has(action)) {
      seen.add(action);
      out.push(action);
    }
  }
  return out;
}
